// SteelOps Invoices screen — the owner-facing list of all invoices for the
// company. Send for payment, mark paid, copy the public /invoice/:token link.

import { useMemo, useState } from 'react';
import { useApp } from '@/context/AppContext';

const SITE_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

function fmtMoneyCents(cents) {
  const n = (Number(cents) || 0) / 100;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}

const STATUS = {
  draft:   { bg: 'var(--s3)',     color: 'var(--t2)',  label: 'Draft' },
  sent:    { bg: 'var(--cyan-t)', color: 'var(--cyan)', label: 'Sent' },
  paid:    { bg: 'var(--teal-t)', color: 'var(--teal)', label: 'Paid' },
  overdue: { bg: 'var(--rose-t)', color: 'var(--rose)', label: 'Overdue' },
  void:    { bg: 'var(--s3)',     color: 'var(--t3)',  label: 'Void' },
};

export default function Billing() {
  const { invoices, custById, markPaid, openModal, showToast } = useApp();
  const [search, setSearch] = useState('');

  const enriched = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return invoices.map(inv => {
      let status = inv.status || 'draft';
      if (status === 'sent' && inv.due && inv.due < today) status = 'overdue';
      return { ...inv, displayStatus: status };
    });
  }, [invoices]);

  const filtered = useMemo(() => {
    if (!search) return enriched;
    const s = search.toLowerCase();
    return enriched.filter(inv => {
      const cust = custById(inv.customerId);
      return (cust?.name || '').toLowerCase().includes(s)
        || (inv.service || '').toLowerCase().includes(s)
        || String(inv.id).includes(s);
    });
  }, [enriched, search, custById]);

  const totals = useMemo(() => {
    const sumCents = (list) => list.reduce((a, i) => a + (Number(i.amount) || 0), 0);
    return {
      paid:    sumCents(enriched.filter(i => i.displayStatus === 'paid')),
      sent:    sumCents(enriched.filter(i => i.displayStatus === 'sent')),
      overdue: sumCents(enriched.filter(i => i.displayStatus === 'overdue')),
    };
  }, [enriched]);

  const copyLink = (token) => {
    if (!token) {
      showToast('No public link yet — try again after the cloud sync completes.', false);
      return;
    }
    const url = `${SITE_ORIGIN}/invoice/${token}`;
    try {
      navigator.clipboard.writeText(url);
      showToast('Public pay link copied');
    } catch {
      showToast(url, true);
    }
  };

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Invoices</div>
          <div className="psub">
            {fmtMoneyCents(totals.paid)} collected · {fmtMoneyCents(totals.overdue)} overdue
          </div>
        </div>
        <div className="pacts">
          <button className="btn btn-primary btn-sm" onClick={() => openModal('add_invoice', {})}>
            + New Invoice
          </button>
        </div>
      </div>

      <div className="g4">
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--teal),transparent)' }} />
          <div className="kpi-label">Paid</div>
          <div className="kpi-value">{fmtMoneyCents(totals.paid)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--cyan),transparent)' }} />
          <div className="kpi-label">Outstanding</div>
          <div className="kpi-value">{fmtMoneyCents(totals.sent)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--rose),transparent)' }} />
          <div className="kpi-label">Overdue</div>
          <div className="kpi-value">{fmtMoneyCents(totals.overdue)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-bar" style={{ background: 'linear-gradient(90deg,var(--violet),transparent)' }} />
          <div className="kpi-label">Total invoices</div>
          <div className="kpi-value">{enriched.length}</div>
        </div>
      </div>

      <div className="card">
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--b1)' }}>
          <div className="search-bar">
            <span>🔍</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search invoices by customer, description, or #…"
            />
          </div>
        </div>
        <div className="card-body-0" style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Service</th>
                <th>Amount</th>
                <th>Issued</th>
                <th>Due</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--t3)', padding: '30px' }}>
                    No invoices yet — create one to start tracking AR.
                  </td>
                </tr>
              )}
              {filtered.map(inv => {
                const c = custById(inv.customerId);
                const sc = STATUS[inv.displayStatus] || STATUS.draft;
                return (
                  <tr key={inv.id}>
                    <td><span className="mono" style={{ fontSize: 11, color: 'var(--t3)' }}>#{inv.id}</span></td>
                    <td><div style={{ fontWeight: 600 }}>{c?.name || '—'}</div></td>
                    <td style={{ color: 'var(--t2)', fontSize: 12 }}>{inv.service || '—'}</td>
                    <td><span className="mono" style={{ fontWeight: 700 }}>{fmtMoneyCents(inv.amount)}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--t3)' }}>{fmtDate(inv.date)}</td>
                    <td style={{ fontSize: 12, color: 'var(--t3)' }}>{fmtDate(inv.due)}</td>
                    <td>
                      <span className="bdg" style={{ background: sc.bg, color: sc.color }}>{sc.label}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {inv.token && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => copyLink(inv.token)}
                            title="Copy the public /invoice/:token pay link"
                          >
                            Copy link
                          </button>
                        )}
                        {inv.displayStatus !== 'paid' && (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => markPaid(inv.id)}
                          >
                            Mark Paid
                          </button>
                        )}
                        {inv.displayStatus === 'paid' && (
                          <span style={{ fontSize: 11, color: 'var(--t3)' }}>Paid {fmtDate(inv.paidDate)}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
